import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import DriveMigrationWizard from "@/pages/settings/DriveMigrationWizard";
import DriveSanitizeBlock from "@/pages/settings/DriveSanitizeBlock";
import AdminDocsMigrationWizard from "@/pages/settings/AdminDocsMigrationWizard";
import DriveHealthBlock from "@/pages/settings/DriveHealthBlock";
import ClientDriveAutoProvisioningBlock from "@/pages/settings/ClientDriveAutoProvisioningBlock";
import PayrollSettingsBlock from "@/pages/settings/PayrollSettingsBlock";
import { FileText, FolderOpen, Save, ExternalLink, Mail, Plus, X, Hash, Receipt, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SettingsHeader } from "./_shared";

export default function MaintenanceSettings({ section = "all" }) {
  // Iter-83: `section` filters which thematic block(s) are rendered. Top-level navigation
  // is now a Sidebar dropdown (mirroring Settings) → each sub-route mounts this component
  // with a single section. Default "all" preserves the legacy single-page rendering.
  const showSection = (s) => section === "all" || section === s;
  const SECTION_TITLES = {
    "general": { kicker: "Maintenance · General", title: "General maintenance", description: "Drive folder + invoice PDF regeneration." },
    "invoice-email": { kicker: "Maintenance · Invoice email", title: "Invoice email configuration", description: "From alias, signature, Cc list and editable subject/body templates for invoice emails." },
    "outgoing-po-email": { kicker: "Maintenance · Outgoing PO email", title: "Outgoing PO email configuration", description: "From alias and editable subject/body templates for the email that sends a Bon de commande (BC) to a supplier." },
    "cra-export": { kicker: "Maintenance · CRA Export", title: "CRA Export configuration", description: "From alias, billing-lock days, Cc list and editable templates for the monthly CRA export email." },
    "weekly-cra": { kicker: "Maintenance · Weekly CRA", title: "Weekly CRA configuration", description: "From alias, Cc list, subject/body templates and reminder schedule for the Weekly CRA email." },
    "monthly-cra": { kicker: "Maintenance · Monthly CRA", title: "Monthly CRA configuration (final clients)", description: "From alias (locked to admin@ba-inmotion.com unless changed here), submitter name, Cc list and subject/body templates for the consolidated monthly CRA email sent to final clients." },
    "freelance-flow": { kicker: "Maintenance · Freelance flow", title: "Freelance unlock & cancellation templates", description: "Configure the working-day grace period, the unlock notification email, the cancellation notification email (with optional Gestionnaire-as-sender mode), and the J+3 / J+7 reminders sent to freelancers who haven't yet submitted." },
    "logs": { kicker: "Maintenance · Logs", title: "Activity logs (current month)", description: "Audit complet du mois en cours : modifications de missions, changements de rôle, factures clients, soumissions/validations de CRA et factures freelance. Tous profils confondus, horodaté. Les mois antérieurs sont archivés sur le dossier Drive configuré dans Général." },
  };
  // Use a hooks-friendly section title (object) — falls back to the legacy header for "all".
  const sectionTitle = SECTION_TITLES[section];
  const { user: currentUser } = useAuth();
  const isCEO = currentUser?.role === "CEO";
  const [regenRunning, setRegenRunning] = useState(false);
  const [regenResult, setRegenResult] = useState(null);
  const [clientInvoicesDriveUrl, setClientInvoicesDriveUrl] = useState("");
  const [savedClientInvoicesDriveUrl, setSavedClientInvoicesDriveUrl] = useState("");
  const [swiftDriveUrl, setSwiftDriveUrl] = useState("");
  const [savedSwiftDriveUrl, setSavedSwiftDriveUrl] = useState("");
  const [pkgDriveUrl, setPkgDriveUrl] = useState("");
  const [savedPkgDriveUrl, setSavedPkgDriveUrl] = useState("");
  const [bankReconDriveUrl, setBankReconDriveUrl] = useState("");
  const [savedBankReconDriveUrl, setSavedBankReconDriveUrl] = useState("");
  const [savingDrive, setSavingDrive] = useState(false);

  // Iter-51: invoice email templates + extra Cc users (CEO-only)
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [savedEmailSubject, setSavedEmailSubject] = useState("");
  const [savedEmailBody, setSavedEmailBody] = useState("");
  // Iter-206n: CEO-configurable "From" alias for invoice emails
  const [emailFromAlias, setEmailFromAlias] = useState("");
  const [savedEmailFromAlias, setSavedEmailFromAlias] = useState("");
  // Iter-206o: Outgoing PO email template (CEO-only, mirrors invoice email)
  const [poEmailFromAlias, setPoEmailFromAlias] = useState("");
  const [poEmailSubject, setPoEmailSubject] = useState("");
  const [poEmailBody, setPoEmailBody] = useState("");
  const [savedPoEmailFromAlias, setSavedPoEmailFromAlias] = useState("");
  const [savedPoEmailSubject, setSavedPoEmailSubject] = useState("");
  const [savedPoEmailBody, setSavedPoEmailBody] = useState("");
  const [savingPoEmail, setSavingPoEmail] = useState(false);
  const [sendingPoTest, setSendingPoTest] = useState(false);
  const [extraCcUserIds, setExtraCcUserIds] = useState([]);
  const [savedExtraCcUserIds, setSavedExtraCcUserIds] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingCc, setSavingCc] = useState(false);
  const [pickerUserId, setPickerUserId] = useState("");

  // Iter-58: invoice email signature editor (CEO-only)
  const [signatureHtml, setSignatureHtml] = useState("");
  const [savedSignatureHtml, setSavedSignatureHtml] = useState("");
  const [logoPosition, setLogoPosition] = useState("inline-left");
  const [savedLogoPosition, setSavedLogoPosition] = useState("inline-left");
  const [signaturePreviewHtml, setSignaturePreviewHtml] = useState("");
  const [savingSignature, setSavingSignature] = useState(false);
  const editorRef = React.useRef(null);

  // Iter-77: CRA Export email config (CEO-only) — From email + Cc + subject/body templates
  const [craFromEmail, setCraFromEmail] = useState("");
  const [savedCraFromEmail, setSavedCraFromEmail] = useState("");
  const [craSubject, setCraSubject] = useState("");
  const [savedCraSubject, setSavedCraSubject] = useState("");
  const [craBody, setCraBody] = useState("");
  const [savedCraBody, setSavedCraBody] = useState("");
  const [craCcIds, setCraCcIds] = useState([]);
  const [savedCraCcIds, setSavedCraCcIds] = useState([]);
  const [craLockDays, setCraLockDays] = useState(5);
  const [savedCraLockDays, setSavedCraLockDays] = useState(5);
  const [savingCraEmail, setSavingCraEmail] = useState(false);
  const [savingCraCc, setSavingCraCc] = useState(false);
  const [craPickerUserId, setCraPickerUserId] = useState("");

  // Iter-80: Weekly CRA email config (CEO-only) — From email + Subject/Body templates
  const [wkFromEmail, setWkFromEmail] = useState("");
  const [savedWkFromEmail, setSavedWkFromEmail] = useState("");
  const [wkSubject, setWkSubject] = useState("");
  const [savedWkSubject, setSavedWkSubject] = useState("");
  const [wkBody, setWkBody] = useState("");
  const [savedWkBody, setSavedWkBody] = useState("");
  const [savingWkEmail, setSavingWkEmail] = useState(false);
  // Iter-82: Weekly CRA extra Cc users (CEO-only) — manager is always added automatically
  const [wkCcIds, setWkCcIds] = useState([]);
  const [savedWkCcIds, setSavedWkCcIds] = useState([]);
  const [wkPickerUserId, setWkPickerUserId] = useState("");
  const [savingWkCc, setSavingWkCc] = useState(false);

  // Iter-81: Weekly CRA reminders schedule (CEO-only)
  const [wkRemEnabled, setWkRemEnabled] = useState(true);
  const [savedWkRemEnabled, setSavedWkRemEnabled] = useState(true);
  const [wkRemInappDay, setWkRemInappDay] = useState(3);
  const [savedWkRemInappDay, setSavedWkRemInappDay] = useState(3);
  const [wkRemInappHour, setWkRemInappHour] = useState(18);
  const [savedWkRemInappHour, setSavedWkRemInappHour] = useState(18);
  const [wkRemEmailDay, setWkRemEmailDay] = useState(4);
  const [savedWkRemEmailDay, setSavedWkRemEmailDay] = useState(4);
  const [wkRemEmailHour, setWkRemEmailHour] = useState(14);
  const [savedWkRemEmailHour, setSavedWkRemEmailHour] = useState(14);
  const [savingWkRem, setSavingWkRem] = useState(false);

  // Iter-151: Monthly CRA email config (CEO-only) for FINAL clients.
  const [monFromEmail, setMonFromEmail] = useState("");
  const [savedMonFromEmail, setSavedMonFromEmail] = useState("");
  const [monSubmittedBy, setMonSubmittedBy] = useState("");
  const [savedMonSubmittedBy, setSavedMonSubmittedBy] = useState("");
  const [monSubject, setMonSubject] = useState("");
  const [savedMonSubject, setSavedMonSubject] = useState("");
  const [monBody, setMonBody] = useState("");
  const [savedMonBody, setSavedMonBody] = useState("");
  const [monCcIds, setMonCcIds] = useState([]);
  const [savedMonCcIds, setSavedMonCcIds] = useState([]);
  const [monPickerUserId, setMonPickerUserId] = useState("");
  const [savingMonEmail, setSavingMonEmail] = useState(false);
  const [savingMonCc, setSavingMonCc] = useState(false);

  // Iter-156: Freelance flow templates (CEO-only) — grace days, unlock email,
  // cancel-notification (with optional Gestionnaire-as-sender mode), J+3 / J+7 reminders.
  const [flGraceDays, setFlGraceDays] = useState(7);
  const [savedFlGraceDays, setSavedFlGraceDays] = useState(7);
  const [flUnlockSubject, setFlUnlockSubject] = useState("");
  const [savedFlUnlockSubject, setSavedFlUnlockSubject] = useState("");
  const [flUnlockBody, setFlUnlockBody] = useState("");
  const [savedFlUnlockBody, setSavedFlUnlockBody] = useState("");
  const [flCancelFromRole, setFlCancelFromRole] = useState("CEO");
  const [savedFlCancelFromRole, setSavedFlCancelFromRole] = useState("CEO");
  const [flCancelSubject, setFlCancelSubject] = useState("");
  const [savedFlCancelSubject, setSavedFlCancelSubject] = useState("");
  const [flCancelBody, setFlCancelBody] = useState("");
  const [savedFlCancelBody, setSavedFlCancelBody] = useState("");
  const [flR1Subject, setFlR1Subject] = useState("");
  const [savedFlR1Subject, setSavedFlR1Subject] = useState("");
  const [flR1Body, setFlR1Body] = useState("");
  const [savedFlR1Body, setSavedFlR1Body] = useState("");
  const [flR2Subject, setFlR2Subject] = useState("");
  const [savedFlR2Subject, setSavedFlR2Subject] = useState("");
  const [flR2Body, setFlR2Body] = useState("");
  const [savedFlR2Body, setSavedFlR2Body] = useState("");
  const [savingFl, setSavingFl] = useState(false);
  // Iter-221 — Workflow status email templates (configurable per event).
  // Meta is loaded from /api/freelance/workflow-templates/meta; values come from `settings`.
  const [wfMeta, setWfMeta] = useState({ events: [], available_variables: [] });
  const [wfDraft, setWfDraft] = useState({}); // {event_key: {subject, body, extra_cc}}
  const [savedWfDraft, setSavedWfDraft] = useState({});
  const [savingWf, setSavingWf] = useState(false);

  // Iter-106: Logs archive Drive URL
  const [logsArchiveUrl, setLogsArchiveUrl] = useState("");
  const [savedLogsArchiveUrl, setSavedLogsArchiveUrl] = useState("");
  const [savingLogsArchive, setSavingLogsArchive] = useState(false);
  // Iter-137: auto-invoice toggles
  const [autoInvoiceEnabled, setAutoInvoiceEnabled] = useState(false);
  const [autoInvoiceShadow, setAutoInvoiceShadow] = useState(false);
  const [savingAutoInvoice, setSavingAutoInvoice] = useState(false);

  // Iter-130: invoice number floor — prevents collisions with manual invoices issued
  // outside the system (e.g. PROD must start at FA-2026-004 because 001..003 were
  // hand-written before the platform went live).
  const [floorYear, setFloorYear] = useState(new Date().getFullYear());
  const [floorMin, setFloorMin] = useState("");
  const [floorPreview, setFloorPreview] = useState(null);
  const [savingFloor, setSavingFloor] = useState(false);

  const loadSettings = async () => {
    try {
      const r = await api.get("/settings");
      const url = r.data?.client_invoices_drive_folder_url || "";
      setClientInvoicesDriveUrl(url);
      setSavedClientInvoicesDriveUrl(url);
      const swUrl = r.data?.swift_drive_folder_url || "";
      setSwiftDriveUrl(swUrl);
      setSavedSwiftDriveUrl(swUrl);
      const pkgUrl = r.data?.statement_package_drive_folder_url || "";
      setPkgDriveUrl(pkgUrl);
      setSavedPkgDriveUrl(pkgUrl);
      const brUrl = r.data?.bank_recon_drive_folder_url || "";
      setBankReconDriveUrl(brUrl);
      setSavedBankReconDriveUrl(brUrl);
      const logsUrl = r.data?.logs_archive_drive_url || "";
      setLogsArchiveUrl(logsUrl);
      setSavedLogsArchiveUrl(logsUrl);
      setEmailSubject(r.data?.invoice_email_subject_template || "");
      setSavedEmailSubject(r.data?.invoice_email_subject_template || "");
      setEmailBody(r.data?.invoice_email_body_template || "");
      setSavedEmailBody(r.data?.invoice_email_body_template || "");
      // Iter-206n: From alias (empty string in DB ⇒ backend falls back to env/default)
      const fromAlias = r.data?.invoice_email_from_alias || "";
      setEmailFromAlias(fromAlias);
      setSavedEmailFromAlias(fromAlias);
      // Iter-206o: Outgoing PO email template
      const poFrom = r.data?.outgoing_po_email_from_alias || "";
      const poSubj = r.data?.outgoing_po_email_subject_template || "";
      const poBody = r.data?.outgoing_po_email_body_template || "";
      setPoEmailFromAlias(poFrom); setSavedPoEmailFromAlias(poFrom);
      setPoEmailSubject(poSubj); setSavedPoEmailSubject(poSubj);
      setPoEmailBody(poBody); setSavedPoEmailBody(poBody);
      const cc = Array.isArray(r.data?.invoice_email_extra_cc_user_ids) ? r.data.invoice_email_extra_cc_user_ids : [];
      setExtraCcUserIds(cc);
      setSavedExtraCcUserIds(cc);
      // Iter-58
      const sigHtml = r.data?.invoice_email_signature_html || "";
      const sigPos = r.data?.invoice_email_signature_logo_position || "inline-left";
      setSignatureHtml(sigHtml);
      setSavedSignatureHtml(sigHtml);
      setLogoPosition(sigPos);
      setSavedLogoPosition(sigPos);
      // Iter-137: auto-invoice toggles
      setAutoInvoiceEnabled(!!r.data?.auto_invoice_enabled);
      setAutoInvoiceShadow(!!r.data?.auto_invoice_shadow_mode);
      // Iter-77: CRA Export email
      const cFrom = r.data?.cra_export_from_email || "facturation@ba-inmotion.com";
      const cSubj = r.data?.cra_export_subject_template || "";
      const cBody = r.data?.cra_export_body_template || "";
      const cCc = Array.isArray(r.data?.cra_export_extra_cc_user_ids) ? r.data.cra_export_extra_cc_user_ids : [];
      const cLock = r.data?.cra_export_billing_lock_business_days ?? 5;
      setCraFromEmail(cFrom); setSavedCraFromEmail(cFrom);
      setCraSubject(cSubj); setSavedCraSubject(cSubj);
      setCraBody(cBody); setSavedCraBody(cBody);
      setCraCcIds(cCc); setSavedCraCcIds(cCc);
      setCraLockDays(cLock); setSavedCraLockDays(cLock);
      // Iter-80: Weekly CRA email
      const wFrom = r.data?.weekly_cra_from_email || "facturation@ba-inmotion.com";
      const wSubj = r.data?.weekly_cra_subject_template || "";
      const wBody = r.data?.weekly_cra_body_template || "";
      setWkFromEmail(wFrom); setSavedWkFromEmail(wFrom);
      setWkSubject(wSubj); setSavedWkSubject(wSubj);
      setWkBody(wBody); setSavedWkBody(wBody);
      // Iter-82: Weekly CRA extra Cc list
      const wCc = Array.isArray(r.data?.weekly_cra_extra_cc_user_ids) ? r.data.weekly_cra_extra_cc_user_ids : [];
      setWkCcIds(wCc); setSavedWkCcIds(wCc);
      // Iter-151: Monthly CRA (final clients) email
      const mFrom = r.data?.monthly_cra_from_email || "admin@ba-inmotion.com";
      const mSubmitted = r.data?.monthly_cra_submitted_by_name || "Sami Khanfir";
      const mSubj = r.data?.monthly_cra_subject_template || "";
      const mBody = r.data?.monthly_cra_body_template || "";
      const mCc = Array.isArray(r.data?.monthly_cra_extra_cc_user_ids) ? r.data.monthly_cra_extra_cc_user_ids : [];
      setMonFromEmail(mFrom); setSavedMonFromEmail(mFrom);
      setMonSubmittedBy(mSubmitted); setSavedMonSubmittedBy(mSubmitted);
      setMonSubject(mSubj); setSavedMonSubject(mSubj);
      setMonBody(mBody); setSavedMonBody(mBody);
      setMonCcIds(mCc); setSavedMonCcIds(mCc);
      // Iter-156: Freelance flow
      const fG = Number(r.data?.freelance_unlock_grace_days || 7);
      const flUS = r.data?.freelance_unlock_email_subject_template || "";
      const flUB = r.data?.freelance_unlock_email_body_template || "";
      const flCR = (r.data?.freelance_cancel_email_from_role || "CEO").toUpperCase();
      const flCS = r.data?.freelance_cancel_email_subject_template || "";
      const flCB = r.data?.freelance_cancel_email_body_template || "";
      const flR1S = r.data?.freelance_reminder1_subject_template || "";
      const flR1B = r.data?.freelance_reminder1_body_template || "";
      const flR2S = r.data?.freelance_reminder2_subject_template || "";
      const flR2B = r.data?.freelance_reminder2_body_template || "";
      setFlGraceDays(fG); setSavedFlGraceDays(fG);
      setFlUnlockSubject(flUS); setSavedFlUnlockSubject(flUS);
      setFlUnlockBody(flUB); setSavedFlUnlockBody(flUB);
      setFlCancelFromRole(flCR); setSavedFlCancelFromRole(flCR);
      setFlCancelSubject(flCS); setSavedFlCancelSubject(flCS);
      setFlCancelBody(flCB); setSavedFlCancelBody(flCB);
      setFlR1Subject(flR1S); setSavedFlR1Subject(flR1S);
      setFlR1Body(flR1B); setSavedFlR1Body(flR1B);
      setFlR2Subject(flR2S); setSavedFlR2Subject(flR2S);
      setFlR2Body(flR2B); setSavedFlR2Body(flR2B);
      // Iter-221 — Hydrate workflow status email templates. Pre-fill the
      // editable fields with the DEFAULTS when no saved override exists so the
      // CEO can edit the proposal directly (rather than seeing a gray placeholder).
      try {
        const meta = await api.get("/freelance/workflow-templates/meta");
        const events = meta.data?.events || [];
        setWfMeta({ events, available_variables: meta.data?.available_variables || [] });
        const draft = {};
        for (const ev of events) {
          draft[ev.event_key] = {
            subject: r.data?.[ev.subject_key] || ev.default_subject || "",
            body: r.data?.[ev.body_key] || ev.default_body || "",
            extra_cc: r.data?.[ev.cc_key] || "",
          };
        }
        setWfDraft(draft);
        setSavedWfDraft(JSON.parse(JSON.stringify(draft)));
      } catch (_e) { /* swallow — endpoint may be missing on legacy backend */ }
      // Iter-81: reminders schedule
      const remEnabled = r.data?.weekly_cra_reminder_enabled ?? true;
      const remInappDay = r.data?.weekly_cra_reminder_inapp_day ?? 3;
      const remInappHour = r.data?.weekly_cra_reminder_inapp_hour ?? 18;
      const remEmailDay = r.data?.weekly_cra_reminder_email_day ?? 4;
      const remEmailHour = r.data?.weekly_cra_reminder_email_hour ?? 14;
      setWkRemEnabled(remEnabled); setSavedWkRemEnabled(remEnabled);
      setWkRemInappDay(remInappDay); setSavedWkRemInappDay(remInappDay);
      setWkRemInappHour(remInappHour); setSavedWkRemInappHour(remInappHour);
      setWkRemEmailDay(remEmailDay); setSavedWkRemEmailDay(remEmailDay);
      setWkRemEmailHour(remEmailHour); setSavedWkRemEmailHour(remEmailHour);
    } catch (_e) { /* swallow */ }
  };
  const loadUsers = async () => {
    if (!isCEO) return;
    try {
      const r = await api.get("/users");
      setAllUsers((r.data || []).filter((u) => u.email));
    } catch (_e) { /* swallow */ }
  };
  useEffect(() => { loadSettings(); loadUsers(); }, [isCEO]);

  // Iter-130: invoice numbering floor — fetch preview whenever the year changes.
  const loadFloorPreview = async (year) => {
    try {
      const r = await api.get(`/billing/numbering/floor?year=${year}`);
      setFloorPreview(r.data);
      const f = (r.data?.floors || {})[String(year)];
      setFloorMin(f ? String(f) : "");
    } catch (_e) { setFloorPreview(null); }
  };
  useEffect(() => { if (isCEO) loadFloorPreview(floorYear); }, [isCEO, floorYear]);

  const saveInvoiceFloor = async () => {
    const yr = parseInt(floorYear, 10);
    const mn = parseInt(floorMin, 10);
    if (!yr || !mn || mn < 1) { toast.error("Year + minimum number required (min ≥ 1)"); return; }
    setSavingFloor(true);
    try {
      // Merge with existing floors so we don't wipe other years.
      const existing = floorPreview?.floors || {};
      const merged = { ...existing, [String(yr)]: mn };
      await api.put("/settings", { invoice_number_floors: merged });
      toast.success(`Floor set: next ${yr} invoice will be ≥ FA-${yr}-${String(mn).padStart(3, "0")}`);
      loadFloorPreview(yr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error saving floor");
    } finally { setSavingFloor(false); }
  };

  const clearInvoiceFloor = async () => {
    const yr = parseInt(floorYear, 10);
    if (!yr) return;
    setSavingFloor(true);
    try {
      const existing = { ...(floorPreview?.floors || {}) };
      delete existing[String(yr)];
      await api.put("/settings", { invoice_number_floors: existing });
      toast.success(`Floor cleared for ${yr}`);
      loadFloorPreview(yr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingFloor(false); }
  };

  const saveClientInvoicesDriveUrl = async () => {
    setSavingDrive(true);
    try {
      await api.put("/settings", {
        client_invoices_drive_folder_url: clientInvoicesDriveUrl.trim(),
        swift_drive_folder_url: swiftDriveUrl.trim(),
        statement_package_drive_folder_url: pkgDriveUrl.trim(),
        bank_recon_drive_folder_url: bankReconDriveUrl.trim(),
      });
      toast.success("Default Drive folder for client invoices saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingDrive(false); }
  };

  // Iter-106
  const saveLogsArchiveUrl = async () => {
    setSavingLogsArchive(true);
    try {
      await api.put("/settings", { logs_archive_drive_url: logsArchiveUrl.trim() });
      toast.success("Logs archive Drive folder saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingLogsArchive(false); }
  };

  const saveEmailTemplate = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Subject and body cannot be empty");
      return;
    }
    // Iter-206n: validate From alias when set (empty means "use env-var/default")
    const trimmedFrom = (emailFromAlias || "").trim();
    if (trimmedFrom && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedFrom)) {
      toast.error("Invalid 'From' email address");
      return;
    }
    setSavingEmail(true);
    try {
      await api.put("/settings", {
        invoice_email_subject_template: emailSubject,
        invoice_email_body_template: emailBody,
        invoice_email_from_alias: trimmedFrom,
      });
      toast.success("Invoice email template saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingEmail(false); }
  };

  const resetEmailTemplate = () => {
    setEmailFromAlias("facturation@ba-inmotion.com");
    setEmailSubject("Facture N° {{invoice_number}} – Prestation {{period}} ({{mission_title}})");
    setEmailBody(
      "Mr. {{contact_name}},\n\n" +
      "Veuillez trouver en pièce jointe la facture N° {{invoice_number}} correspondant aux prestations réalisées par nos services durant le mois de {{period}} dans le cadre de la mission {{mission_title}}.\n\n" +
      "Ce document a été établi conformément aux termes de notre devis N° {{po_number}}.\n\n" +
      "Récapitulatif de la facturation :\n\n" +
      "  Période : {{period}} ({{total_jh}} jours)\n\n" +
      "  Montant HT  : {{total_ht}} {{currency}}\n\n" +
      "  Montant TTC : {{total_ttc}} {{currency}}\n\n" +
      "Je reste à votre disposition pour tout complément d'information et pour le suivi du règlement.\n\n" +
      "Cordialement,\n"
    );
  };

  // Iter-77: CRA Export email handlers
  const saveCraEmail = async () => {
    if (!craSubject.trim() || !craBody.trim()) {
      toast.error("CRA Export subject and body cannot be empty");
      return;
    }
    setSavingCraEmail(true);
    try {
      await api.put("/settings", {
        cra_export_from_email: craFromEmail.trim(),
        cra_export_subject_template: craSubject,
        cra_export_body_template: craBody,
        cra_export_billing_lock_business_days: parseInt(craLockDays, 10) || 5,
      });
      toast.success("CRA Export email config saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingCraEmail(false); }
  };
  const resetCraEmail = () => {
    setCraSubject("CRA {{period}} — {{mission_title}}");
    setCraBody(
      "Bonjour,\n\n" +
      "Merci de trouver ci-joint le CRA du mois de {{period}} pour la mission {{mission_title}}. " +
      "La facture en question vous sera transmise prochainement.\n\n" +
      "Bonne réception,\n"
    );
  };
  const addCraCc = () => {
    if (!craPickerUserId || craCcIds.includes(craPickerUserId)) return;
    setCraCcIds([...craCcIds, craPickerUserId]);
    setCraPickerUserId("");
  };
  const removeCraCc = (uid) => setCraCcIds(craCcIds.filter((x) => x !== uid));
  const saveCraCc = async () => {
    setSavingCraCc(true);
    try {
      await api.put("/settings", { cra_export_extra_cc_user_ids: craCcIds });
      toast.success("CRA Export Cc list saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingCraCc(false); }
  };

  // Iter-82: Weekly CRA extra Cc handlers
  const addWkCc = () => {
    if (!wkPickerUserId || wkCcIds.includes(wkPickerUserId)) return;
    setWkCcIds([...wkCcIds, wkPickerUserId]);
    setWkPickerUserId("");
  };
  const removeWkCc = (uid) => setWkCcIds(wkCcIds.filter((x) => x !== uid));
  const saveWkCc = async () => {
    setSavingWkCc(true);
    try {
      await api.put("/settings", { weekly_cra_extra_cc_user_ids: wkCcIds });
      toast.success("Weekly CRA Cc list saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingWkCc(false); }
  };

  // Iter-151: Monthly CRA email handlers (FINAL clients).
  const addMonCc = () => {
    if (!monPickerUserId || monCcIds.includes(monPickerUserId)) return;
    setMonCcIds([...monCcIds, monPickerUserId]);
    setMonPickerUserId("");
  };
  const removeMonCc = (uid) => setMonCcIds(monCcIds.filter((x) => x !== uid));
  const saveMonEmail = async () => {
    if (!monFromEmail.trim() || !monSubject.trim() || !monBody.trim() || !monSubmittedBy.trim()) {
      toast.error("From email, submitter name, subject and body are all required");
      return;
    }
    setSavingMonEmail(true);
    try {
      await api.put("/settings", {
        monthly_cra_from_email: monFromEmail.trim(),
        monthly_cra_submitted_by_name: monSubmittedBy.trim(),
        monthly_cra_subject_template: monSubject,
        monthly_cra_body_template: monBody,
      });
      toast.success("Monthly CRA configuration saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingMonEmail(false); }
  };
  const saveMonCc = async () => {
    setSavingMonCc(true);
    try {
      await api.put("/settings", { monthly_cra_extra_cc_user_ids: monCcIds });
      toast.success("Monthly CRA Cc list saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingMonCc(false); }
  };
  const resetMonEmail = () => {
    setMonFromEmail("admin@ba-inmotion.com");
    setMonSubmittedBy("Sami Khanfir");
    setMonSubject("CRA mensuel — {{mission_title}} — {{period}}");
    setMonBody("Bonjour {{contact_name}},\n\nVeuillez trouver ci-joint le compte rendu d'activité consolidé de l'équipe BAIM pour la mission {{mission_title}} sur le mois de {{period}}.\n\nBonne réception,\nL'équipe BAIM\n");
  };

  // Iter-156: Freelance flow save handler.
  const flDirty = (
    flGraceDays !== savedFlGraceDays ||
    flUnlockSubject !== savedFlUnlockSubject ||
    flUnlockBody !== savedFlUnlockBody ||
    flCancelFromRole !== savedFlCancelFromRole ||
    flCancelSubject !== savedFlCancelSubject ||
    flCancelBody !== savedFlCancelBody ||
    flR1Subject !== savedFlR1Subject ||
    flR1Body !== savedFlR1Body ||
    flR2Subject !== savedFlR2Subject ||
    flR2Body !== savedFlR2Body
  );
  // Iter-221d — Unified "Save freelance flow" handler that persists both the
  // legacy templates (sections 1-4) and the workflow status templates (5-8) in
  // a single PUT call. `flDirty || wfDirty` controls the global Save button.
  const saveFlowFreelance = async () => {
    const grace = parseInt(flGraceDays, 10);
    if (Number.isNaN(grace) || grace < 0 || grace > 60) {
      toast.error("Grace days doit être un entier entre 0 et 60");
      return;
    }
    setSavingFl(true);
    try {
      const payload = {
        freelance_unlock_grace_days: grace,
        freelance_unlock_email_subject_template: flUnlockSubject,
        freelance_unlock_email_body_template: flUnlockBody,
        freelance_cancel_email_from_role: flCancelFromRole,
        freelance_cancel_email_subject_template: flCancelSubject,
        freelance_cancel_email_body_template: flCancelBody,
        freelance_reminder1_subject_template: flR1Subject,
        freelance_reminder1_body_template: flR1Body,
        freelance_reminder2_subject_template: flR2Subject,
        freelance_reminder2_body_template: flR2Body,
      };
      for (const ev of (wfMeta.events || [])) {
        const d = wfDraft[ev.event_key] || { subject: "", body: "", extra_cc: "" };
        payload[ev.subject_key] = d.subject || "";
        payload[ev.body_key] = d.body || "";
        payload[ev.cc_key] = d.extra_cc || "";
      }
      await api.put("/settings", payload);
      toast.success("Freelance flow saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingFl(false); }
  };

  // Iter-221 — Save workflow status email templates (folded into the unified Save).
  const wfDirty = useMemo(() => {
    return JSON.stringify(wfDraft) !== JSON.stringify(savedWfDraft);
  }, [wfDraft, savedWfDraft]);
  const resetWorkflowEventToDefault = (ev) => {
    setWfDraft((prev) => ({
      ...prev,
      [ev.event_key]: {
        subject: ev.default_subject || "",
        body: ev.default_body || "",
        extra_cc: prev[ev.event_key]?.extra_cc || "",
      },
    }));
  };

  // Iter-80: Weekly CRA email handlers
  const saveWkEmail = async () => {
    if (!wkSubject.trim() || !wkBody.trim()) {
      toast.error("Weekly CRA subject and body cannot be empty");
      return;
    }
    setSavingWkEmail(true);
    try {
      await api.put("/settings", {
        weekly_cra_subject_template: wkSubject,
        weekly_cra_body_template: wkBody,
      });
      toast.success("Weekly CRA email config saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingWkEmail(false); }
  };
  const resetWkEmail = () => {
    setWkSubject("CRA Semaine {{iso_week}} — {{mission_title}} ({{period}})");
    setWkBody(
      "Bonjour,\n\n" +
      "Veuillez trouver ci-joint mon compte rendu d'activité pour la semaine {{iso_week}} ({{period}}) sur la mission {{mission_title}}.\n\n" +
      "Total des heures travaillées : {{total_hours}}h.\n\n" +
      "Bonne réception,\n" +
      "{{user_name}}\n"
    );
  };

  // Iter-81: Weekly CRA reminders save
  const saveWkReminders = async () => {
    setSavingWkRem(true);
    try {
      await api.put("/settings", {
        weekly_cra_reminder_enabled: !!wkRemEnabled,
        weekly_cra_reminder_inapp_day: parseInt(wkRemInappDay, 10),
        weekly_cra_reminder_inapp_hour: parseInt(wkRemInappHour, 10),
        weekly_cra_reminder_email_day: parseInt(wkRemEmailDay, 10),
        weekly_cra_reminder_email_hour: parseInt(wkRemEmailHour, 10),
      });
      toast.success("Weekly CRA reminders saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingWkRem(false); }
  };


  const addCcUser = () => {
    if (!pickerUserId || extraCcUserIds.includes(pickerUserId)) return;
    setExtraCcUserIds([...extraCcUserIds, pickerUserId]);
    setPickerUserId("");
  };
  const removeCcUser = (uid) => {
    setExtraCcUserIds(extraCcUserIds.filter((x) => x !== uid));
  };
  const saveCcList = async () => {
    setSavingCc(true);
    try {
      await api.put("/settings", { invoice_email_extra_cc_user_ids: extraCcUserIds });
      toast.success("Cc users updated");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingCc(false); }
  };

  const regenerateInvoicePdfs = async () => {
    if (!window.confirm("Regenerate every existing invoice PDF? Local files will be overwritten (Google Drive untouched).")) return;
    setRegenRunning(true);
    setRegenResult(null);
    try {
      const r = await api.post("/invoices/regenerate-pdfs");
      setRegenResult(r.data);
      if (r.data.failed === 0) toast.success(`${r.data.ok} facture(s) regenerated(s)`);
      else toast.warning(`${r.data.ok} ok · ${r.data.failed} failure(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setRegenRunning(false); }
  };

  const isDirty = clientInvoicesDriveUrl.trim() !== savedClientInvoicesDriveUrl.trim() || swiftDriveUrl.trim() !== savedSwiftDriveUrl.trim() || pkgDriveUrl.trim() !== savedPkgDriveUrl.trim() || bankReconDriveUrl.trim() !== savedBankReconDriveUrl.trim();
  const isEmailDirty = emailSubject !== savedEmailSubject || emailBody !== savedEmailBody || emailFromAlias !== savedEmailFromAlias;

  // Iter-206o: Outgoing PO email handlers ---------------------------------
  const isPoEmailDirty =
    poEmailFromAlias !== savedPoEmailFromAlias ||
    poEmailSubject !== savedPoEmailSubject ||
    poEmailBody !== savedPoEmailBody;

  const savePoEmailTemplate = async () => {
    if (!poEmailSubject.trim() || !poEmailBody.trim()) {
      toast.error("Subject and body cannot be empty");
      return;
    }
    const trimmedFrom = (poEmailFromAlias || "").trim();
    if (trimmedFrom && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedFrom)) {
      toast.error("Invalid 'From' email address");
      return;
    }
    setSavingPoEmail(true);
    try {
      await api.put("/settings", {
        outgoing_po_email_from_alias: trimmedFrom,
        outgoing_po_email_subject_template: poEmailSubject,
        outgoing_po_email_body_template: poEmailBody,
      });
      toast.success("Outgoing PO email template saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingPoEmail(false); }
  };

  const resetPoEmailTemplate = () => {
    setPoEmailFromAlias("facturation@ba-inmotion.com");
    setPoEmailSubject("Bon de commande {{po_number}} — {{supplier_name}}");
    setPoEmailBody(
      "Bonjour {{contact_name}},\n\n" +
      "Veuillez trouver ci-joint notre bon de commande N° {{po_number}} en date du {{po_date}}.\n\n" +
      "Récapitulatif :\n" +
      "  Montant HT  : {{total_ht}} {{currency}}\n" +
      "  TVA         : {{total_vat}} {{currency}}\n" +
      "  Montant TTC : {{total_ttc}} {{currency}}\n" +
      "  Livraison souhaitée : {{delivery_date}}\n\n" +
      "Merci de bien vouloir nous confirmer la bonne réception de cette commande.\n\n" +
      "Cordialement,\n" +
      "{{sender_name}}\n"
    );
  };

  const sendTestPoEmail = async () => {
    setSendingPoTest(true);
    try {
      const r = await api.post("/outgoing-pos/settings/email/send-test");
      toast.success(`Test sent to ${r.data?.to || "your address"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSendingPoTest(false); }
  };
  const isCcDirty = JSON.stringify(extraCcUserIds) !== JSON.stringify(savedExtraCcUserIds);
  const userById = (uid) => allUsers.find((u) => u.user_id === uid);
  const availableUsers = allUsers.filter((u) => !extraCcUserIds.includes(u.user_id));

  // Iter-58 — signature editor handlers
  const isSignatureDirty = signatureHtml !== savedSignatureHtml || logoPosition !== savedLogoPosition;
  const exec = (cmd, val = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, val);
    if (editorRef.current) setSignatureHtml(editorRef.current.innerHTML);
  };
  const refreshSignaturePreview = async () => {
    if (!isCEO) return;
    try {
      const r = await api.post("/settings/invoice-email-signature/preview", {
        signature_html: signatureHtml,
        logo_position: logoPosition,
      });
      setSignaturePreviewHtml(r.data?.signature_html || "");
      toast.success("Preview updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };
  // Iter-58 fix: seed the contentEditable ONLY on mount + when the saved signature changes
  // from the API. We must NOT use dangerouslySetInnerHTML (it would re-set innerHTML on every
  // keystroke and clobber the caret position, breaking the editor).
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== savedSignatureHtml) {
      editorRef.current.innerHTML = savedSignatureHtml;
      setSignatureHtml(savedSignatureHtml);
    }
    // Trigger an initial preview render so the user sees something on first open.
    if (isCEO) {
      (async () => {
        try {
          const r = await api.post("/settings/invoice-email-signature/preview", {
            signature_html: savedSignatureHtml,
            logo_position: savedLogoPosition,
          });
          setSignaturePreviewHtml(r.data?.signature_html || "");
        } catch (_e) { /* swallow */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSignatureHtml, savedLogoPosition, isCEO]);
  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      await api.put("/settings", {
        invoice_email_signature_html: signatureHtml,
        invoice_email_signature_logo_position: logoPosition,
      });
      toast.success("Signature saved");
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingSignature(false); }
  };
  const resetSignature = () => {
    setSignatureHtml("");
    setLogoPosition("inline-left");
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  // Iter-59: send a sample invoice email (mock data, no PDF) to the connected user's mailbox
  // to validate the signature / from-alias / templates without generating a real invoice.
  const [sendingTest, setSendingTest] = useState(false);
  const sendTestInvoiceEmail = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/settings/invoice-email/send-test");
      toast.success(`Test email sent to ${r.data?.to || "your inbox"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Send failed";
      if (String(detail).includes("re-authenticate") || String(detail).includes("MissingScope") || String(detail).includes("scope")) {
        toast.error("Google Gmail authorization required. Please connect Gmail from the top-right menu first.");
      } else {
        toast.error(detail);
      }
    } finally { setSendingTest(false); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[900px]" data-testid={`settings-page-maintenance${section !== "all" ? "-" + section : ""}`}>
      <SettingsHeader
        kicker={sectionTitle?.kicker || "Maintenance"}
        title={sectionTitle?.title || "Invoice maintenance"}
        description={sectionTitle?.description || "Default storage location and regeneration tools for client invoices."}
      />

      {/* Iter-157 — Drive migration wizard (CEO only) ------------------------------ */}
      {isCEO && showSection("general") && (
        <DriveMigrationWizard />
      )}

      {/* Iter-229 — Drive tree sanitizer (CEO only) -------------------------------- */}
      {isCEO && showSection("general") && (
        <DriveSanitizeBlock />
      )}

      {/* Iter-194n — Admin documents migration wizard (CEO only) ------------------- */}
      {isCEO && showSection("general") && (
        <AdminDocsMigrationWizard />
      )}

      {/* Iter-160 — Drive health dashboard (CEO/ADMIN) ----------------------------- */}
      {showSection("general") && (
        <DriveHealthBlock />
      )}

      {/* Iter-194i — Drive auto-provisioning for new clients (CEO-only) ----------- */}
      {isCEO && showSection("general") && (
        <ClientDriveAutoProvisioningBlock />
      )}

      {/* Iter-190 — Payroll CNSS settings (CEO only) ------------------------------ */}
      {isCEO && showSection("general") && (
        <PayrollSettingsBlock />
      )}

      {/* Client invoices Drive folder ---------------------------------------------- */}
      {showSection("general") && (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="client-invoices-drive-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <FolderOpen size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Default Drive folder · Client invoices</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
          Every generated client invoice PDF is auto-uploaded to this shared Google Drive folder right after creation.
          Paste the full <code className="px-1 py-0.5 bg-zinc-100 rounded font-mono">https://drive.google.com/drive/folders/&lt;ID&gt;</code> URL.
          The folder must be shared with the BAIM Workspace user generating the invoice.
        </p>

        <div className="space-y-2">
          <Label className="text-xs">Shared folder URL</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="settings-client-invoices-drive-url"
              value={clientInvoicesDriveUrl}
              onChange={(e) => setClientInvoicesDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="flex-1 font-mono text-xs"
            />
            {savedClientInvoicesDriveUrl && (
              <a
                href={savedClientInvoicesDriveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-client-invoices-drive-open"
                title="Open the configured folder in Google Drive"
                className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t border-zinc-100 pt-4">
          <Label className="text-xs">Swift folder · Payment proofs</Label>
          <p className="text-[11px] text-zinc-500 max-w-2xl leading-relaxed">
            Proofs attached in the « Mark as paid » dialog (SWIFT copies, transfer receipts — PDF or image) are stored here.
            Leave empty: a « Swift » subfolder is auto-created under the folder above on first upload.
          </p>
          <div className="flex items-center gap-2">
            <Input
              data-testid="settings-swift-drive-url"
              value={swiftDriveUrl}
              onChange={(e) => setSwiftDriveUrl(e.target.value)}
              placeholder="Auto — « Swift » under the Client invoices folder"
              className="flex-1 font-mono text-xs"
            />
            {savedSwiftDriveUrl && (
              <a
                href={savedSwiftDriveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-swift-drive-open"
                title="Open the Swift folder in Google Drive"
                className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t border-zinc-100 pt-4">
          <Label className="text-xs">Pièces justificatives · État mensuel (ZIP)</Label>
          <p className="text-[11px] text-zinc-500 max-w-2xl leading-relaxed">
            The supporting-documents ZIP generated when sending the monthly statement (client invoices, supplier invoices, expense notes + receipts, payment proofs)
            is archived here under an auto-created <code className="px-1 py-0.5 bg-zinc-100 rounded font-mono">{"{YYYY_MM}"}</code> subfolder.
          </p>
          <div className="flex items-center gap-2">
            <Input
              data-testid="settings-statement-package-drive-url"
              value={pkgDriveUrl}
              onChange={(e) => setPkgDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="flex-1 font-mono text-xs"
            />
            {savedPkgDriveUrl && (
              <a
                href={savedPkgDriveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-statement-package-drive-open"
                title="Open the statement packages folder in Google Drive"
                className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t border-zinc-100 pt-4">
          <Label className="text-xs">Justificatifs · Rapprochement bancaire (État mensuel)</Label>
          <p className="text-[11px] text-zinc-500 max-w-2xl leading-relaxed">
            Receipts uploaded on « Sans justif » expense lines coming from an uploaded bank statement (bank fees, undeclared expenses)
            are archived in this Drive folder.
          </p>
          <div className="flex items-center gap-2">
            <Input
              data-testid="settings-bank-recon-drive-url"
              value={bankReconDriveUrl}
              onChange={(e) => setBankReconDriveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="flex-1 font-mono text-xs"
            />
            {savedBankReconDriveUrl && (
              <a
                href={savedBankReconDriveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-bank-recon-drive-open"
                title="Open the bank reconciliation receipts folder in Google Drive"
                className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <span className="text-[11px] font-mono text-zinc-500">
            {isDirty ? "Unsaved changes" : "Saved"}
          </span>
          <button
            onClick={saveClientInvoicesDriveUrl}
            disabled={savingDrive || !isDirty}
            data-testid="settings-client-invoices-drive-save"
            className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            <Save size={14} /> {savingDrive ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      )}

      {/* Iter-106: Logs archive Drive folder ----------------------------------- */}
      {showSection("general") && (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="logs-archive-drive-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <FolderOpen size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Logs archive · Drive folder</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
          The <strong>Maintenance · Logs</strong> page shows only the <strong>current month</strong>. Older months should be exported to a shared Google Drive folder (PDF / CSV / spreadsheet).
          Paste the folder URL below — it will appear as a clickable link on the Logs page so anyone with access can browse the archive in one click.
        </p>
        <div className="space-y-2">
          <Label className="text-xs">Shared archive folder URL</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="settings-logs-archive-drive-url"
              value={logsArchiveUrl}
              onChange={(e) => setLogsArchiveUrl(e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              className="flex-1 font-mono text-xs"
            />
            {savedLogsArchiveUrl && (
              <a
                href={savedLogsArchiveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-logs-archive-drive-open"
                title="Open the configured archive folder"
                className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50"
              >
                <ExternalLink size={12} /> Open
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
          <span className="text-[11px] font-mono text-zinc-500">
            {logsArchiveUrl !== savedLogsArchiveUrl ? "Unsaved changes" : "Saved"}
          </span>
          <button
            onClick={saveLogsArchiveUrl}
            disabled={savingLogsArchive || logsArchiveUrl === savedLogsArchiveUrl}
            data-testid="settings-logs-archive-drive-save"
            className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            <Save size={14} /> {savingLogsArchive ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      )}

      {/* Invoice email template (CEO only) ------------------------------------- */}
      {isCEO && showSection("invoice-email") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="invoice-email-template-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Invoice email · Subject &amp; body template</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Customize the wording used by the <strong>Send</strong> button on the Invoices page. Available variables (insert verbatim):
            <code className="ml-1 text-[10px] font-mono">{"{{contact_gender}} {{contact_name}} {{invoice_number}} {{period}} {{mission_title}} {{po_number}} {{currency}} {{total_ht}} {{total_ttc}} {{total_jh}} {{payment_declaration_url}}"}</code>
          </p>
          <div className="space-y-2">
            <Label className="text-xs">From <span className="text-zinc-500 font-normal">(sender alias)</span></Label>
            <Input
              data-testid="settings-email-from-alias"
              type="email"
              value={emailFromAlias}
              onChange={(e) => setEmailFromAlias(e.target.value)}
              className="font-mono text-xs"
              placeholder="facturation@ba-inmotion.com"
            />
            <div className="text-[10px] text-zinc-500 italic leading-relaxed">
              The Gmail account performing the send must be authorized to send AS this alias
              (Workspace admin → "Send Mail As"). Leave empty to fall back to the platform
              default (<code className="font-mono">facturation@ba-inmotion.com</code>).
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Subject template</Label>
            <Input
              data-testid="settings-email-subject-template"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="font-mono text-xs"
              placeholder="Facture N° {{invoice_number}} – Prestation {{period}} ({{mission_title}})"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Body template</Label>
            <Textarea
              data-testid="settings-email-body-template"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              className="font-mono text-[11px] leading-relaxed min-h-[260px] resize-y"
              spellCheck={false}
            />
            <div className="text-[10px] text-zinc-500 italic">The HTML signature configured below (with optional inline BAIM logo) is appended automatically.</div>
          </div>
          <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
            <div className="flex items-center gap-3">
              <button onClick={resetEmailTemplate} data-testid="settings-email-reset" className="text-[11px] font-mono text-zinc-500 hover:text-zinc-800 uppercase">Reset to default</button>
              <button
                onClick={sendTestInvoiceEmail}
                disabled={sendingTest}
                data-testid="settings-email-send-test"
                className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
                title={`A sample invoice email (with mock data) will be sent to ${currentUser?.email || "your address"}.`}
              >
                {sendingTest ? "Sending…" : "Send a preview to me"}
              </button>
            </div>
            <button onClick={saveEmailTemplate} disabled={savingEmail || !isEmailDirty} data-testid="settings-email-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {savingEmail ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>
      )}

      {/* Invoice email signature editor (CEO only) — Iter-58 ---------------- */}
      {isCEO && showSection("invoice-email") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="invoice-email-signature-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Invoice email · Signature</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Compose the HTML signature appended at the end of every invoice email and pick where the BAIM logo should sit relative to the text. Leaving the editor empty falls back to the legacy BAIM signature block.
          </p>

          {/* Logo position picker */}
          <div className="space-y-2" data-testid="signature-logo-position-block">
            <Label className="text-xs">Logo position</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "top", label: "Top" },
                { v: "inline-left", label: "Inline (left)" },
                { v: "bottom", label: "Bottom" },
                { v: "none", label: "No logo" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setLogoPosition(opt.v)}
                  data-testid={`signature-logo-pos-${opt.v}`}
                  className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${
                    logoPosition === opt.v
                      ? "bg-brand-navy text-white"
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Toolbar + contentEditable HTML editor */}
          <div className="space-y-2">
            <Label className="text-xs">Signature content</Label>
            <div className="border border-zinc-200 rounded-md overflow-hidden">
              <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-zinc-100 bg-zinc-50" data-testid="signature-toolbar">
                <button type="button" onClick={() => exec("bold")} data-testid="sig-btn-bold" title="Bold" className="w-8 h-8 rounded hover:bg-zinc-200 font-bold text-sm">B</button>
                <button type="button" onClick={() => exec("italic")} data-testid="sig-btn-italic" title="Italic" className="w-8 h-8 rounded hover:bg-zinc-200 italic text-sm">I</button>
                <button type="button" onClick={() => exec("underline")} data-testid="sig-btn-underline" title="Underline" className="w-8 h-8 rounded hover:bg-zinc-200 underline text-sm">U</button>
                <span className="w-px h-5 bg-zinc-300 mx-1" />
                <label className="inline-flex items-center gap-1 text-[10px] uppercase font-mono text-zinc-500 cursor-pointer">
                  <span>Color</span>
                  <input type="color" defaultValue="#1e5db4" onChange={(e) => exec("foreColor", e.target.value)} data-testid="sig-btn-color" className="w-6 h-6 rounded border border-zinc-200 cursor-pointer" />
                </label>
                <span className="w-px h-5 bg-zinc-300 mx-1" />
                <button type="button" onClick={() => { const url = window.prompt("Link URL", "https://"); if (url) exec("createLink", url); }} data-testid="sig-btn-link" title="Insert link" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs">Link</button>
                <button type="button" onClick={() => exec("removeFormat")} data-testid="sig-btn-clear" title="Clear formatting" className="px-2 h-8 rounded hover:bg-zinc-200 text-xs text-zinc-500">Clear</button>
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-testid="sig-editor"
                onInput={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                onBlur={(e) => setSignatureHtml(e.currentTarget.innerHTML)}
                className="min-h-[180px] p-3 text-sm focus:outline-none"
                style={{ fontFamily: "Verdana, Geneva, sans-serif" }}
              />
            </div>
            <div className="text-[10px] text-zinc-500 italic">
              Tip: type your contact details (e.g. <em>Sami KHANFIR — CEO BAIM — sami@ba-inmotion.ma</em>) here. The BAIM logo is added automatically at the chosen position.
            </div>
          </div>

          {/* Live preview */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Preview</Label>
              <button
                type="button"
                onClick={refreshSignaturePreview}
                data-testid="sig-preview-refresh"
                className="inline-flex items-center gap-1.5 px-3 h-8 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider rounded-md hover:bg-zinc-50"
              >
                Apply &amp; preview
              </button>
            </div>
            <div
              className="border border-dashed border-zinc-300 rounded-md p-4 bg-zinc-50/50 min-h-[120px]"
              data-testid="sig-preview"
              dangerouslySetInnerHTML={{ __html: signaturePreviewHtml }}
            />
          </div>

          <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
            <button onClick={resetSignature} data-testid="sig-reset" className="text-[11px] font-mono text-zinc-500 hover:text-zinc-800 uppercase">Reset to default</button>
            <button onClick={saveSignature} disabled={savingSignature || !isSignatureDirty} data-testid="sig-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {savingSignature ? "Saving…" : "Save signature"}
            </button>
          </div>
        </div>
      )}


      {/* Iter-206o — Outgoing PO email template (CEO only) ------------- */}
      {isCEO && showSection("outgoing-po-email") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="po-email-template-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Outgoing PO email · From, Subject &amp; Body</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Customize the email sent when issuing a <strong>Bon de commande</strong> (BC) to a
            supplier from the <em>Outgoing POs</em> page. Available variables (insert verbatim):
            <code className="ml-1 text-[10px] font-mono">{"{{po_number}} {{supplier_name}} {{contact_name}} {{po_date}} {{delivery_date}} {{currency}} {{total_ht}} {{total_vat}} {{total_ttc}} {{notes}} {{sender_name}}"}</code>
          </p>
          <div className="space-y-2">
            <Label className="text-xs">From <span className="text-zinc-500 font-normal">(sender alias)</span></Label>
            <Input
              data-testid="po-email-from-alias"
              type="email"
              value={poEmailFromAlias}
              onChange={(e) => setPoEmailFromAlias(e.target.value)}
              className="font-mono text-xs"
              placeholder="facturation@ba-inmotion.com"
            />
            <div className="text-[10px] text-zinc-500 italic leading-relaxed">
              Must be authorized via Workspace admin → "Send Mail As". Leave empty to fall back
              first to the <strong>Invoice email</strong> From, then to the platform default
              (<code className="font-mono">facturation@ba-inmotion.com</code>).
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Subject template</Label>
            <Input
              data-testid="po-email-subject-template"
              value={poEmailSubject}
              onChange={(e) => setPoEmailSubject(e.target.value)}
              className="font-mono text-xs"
              placeholder="Bon de commande {{po_number}} — {{supplier_name}}"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Body template</Label>
            <Textarea
              data-testid="po-email-body-template"
              value={poEmailBody}
              onChange={(e) => setPoEmailBody(e.target.value)}
              className="font-mono text-[11px] leading-relaxed min-h-[280px] resize-y"
              spellCheck={false}
            />
            <div className="text-[10px] text-zinc-500 italic">
              The signed PDF of the Bon de commande is attached automatically. No HTML signature is
              appended to PO emails — they go out as plain-text + simple HTML.
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
            <div className="flex items-center gap-3">
              <button onClick={resetPoEmailTemplate} data-testid="po-email-reset" className="text-[11px] font-mono text-zinc-500 hover:text-zinc-800 uppercase">Reset to default</button>
              <button
                onClick={sendTestPoEmail}
                disabled={sendingPoTest}
                data-testid="po-email-send-test"
                className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-[11px] font-mono uppercase tracking-wider text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
                title={`A sample BC email (with mock data, no PDF) will be sent to ${currentUser?.email || "your address"}.`}
              >
                {sendingPoTest ? "Sending…" : "Send a preview to me"}
              </button>
            </div>
            <button onClick={savePoEmailTemplate} disabled={savingPoEmail || !isPoEmailDirty} data-testid="po-email-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {savingPoEmail ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>
      )}

      {/* Extra Cc users (CEO only) ------------------------------------- */}
      {isCEO && showSection("invoice-email") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="invoice-email-cc-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Invoice email · Extra Cc recipients</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Additional users to add in <strong>Cc</strong> on every invoice email. <strong>This is the only way to add Cc on invoice emails</strong> — no recipient is auto-CC'd anymore.
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="settings-cc-list">
            {extraCcUserIds.length === 0 && (
              <span className="text-[11px] text-zinc-400 italic">No additional Cc users configured</span>
            )}
            {extraCcUserIds.map((uid) => {
              const u = userById(uid);
              return (
                <span key={uid} className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1 text-[11px] rounded font-mono" data-testid={`settings-cc-pill-${uid}`}>
                  {u ? `${u.name} <${u.email}>` : uid}
                  <button onClick={() => removeCcUser(uid)} className="hover:text-red-700" data-testid={`settings-cc-remove-${uid}`}><X size={11} /></button>
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Select value={pickerUserId} onValueChange={setPickerUserId}>
              <SelectTrigger data-testid="settings-cc-picker" className="flex-1 max-w-md text-xs">
                <SelectValue placeholder="Pick a user to add…" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No more users to add</div>}
                {availableUsers.map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name} <span className="text-zinc-500">({u.email})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={addCcUser} disabled={!pickerUserId} data-testid="settings-cc-add" className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
            <button onClick={saveCcList} disabled={savingCc || !isCcDirty} data-testid="settings-cc-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {savingCc ? "Saving…" : "Save Cc list"}
            </button>
          </div>
        </div>
      )}

      {/* CRA Export email — From + Subject/Body template (CEO-only) -------------- */}
      {isCEO && showSection("cra-export") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="cra-export-email-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">CRA Export · From email + Subject &amp; body template</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Email used when exporting a CRA to a subcontractor client. Variables available: <code>{`{{period}}`}</code>, <code>{`{{mission_title}}`}</code>, <code>{`{{client_name}}`}</code>, <code>{`{{contact_gender}}`}</code>, <code>{`{{contact_name}}`}</code>, <code>{`{{year}}`}</code>, <code>{`{{month}}`}</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>From email <span className="text-xs text-zinc-500">(must be a verified Gmail "Send mail as" alias)</span></Label>
              <Input
                data-testid="settings-cra-from-email"
                type="email"
                placeholder="facturation@ba-inmotion.com"
                value={craFromEmail}
                onChange={(e) => setCraFromEmail(e.target.value)}
              />
            </div>
            <div>
              <Label>Billing lock — business days delay</Label>
              <Input
                data-testid="settings-cra-lock-days"
                type="number"
                min="0"
                max="30"
                value={craLockDays}
                onChange={(e) => setCraLockDays(e.target.value)}
              />
              <p className="text-[10px] text-zinc-500 mt-1">After CRA export, the mission can't be invoiced for this many working days (skipping weekends + holidays).</p>
            </div>
          </div>
          <div>
            <Label>Subject template</Label>
            <Input
              data-testid="settings-cra-subject"
              value={craSubject}
              onChange={(e) => setCraSubject(e.target.value)}
              placeholder="CRA {{period}} — {{mission_title}}"
            />
          </div>
          <div>
            <Label>Body template</Label>
            <Textarea
              data-testid="settings-cra-body"
              rows={9}
              value={craBody}
              onChange={(e) => setCraBody(e.target.value)}
              placeholder="Bonjour, ..."
              className="font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between border-t border-zinc-100 pt-4 gap-2 flex-wrap">
            <button onClick={resetCraEmail} data-testid="settings-cra-email-reset" className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
              Reset to default
            </button>
            <button
              onClick={saveCraEmail}
              disabled={savingCraEmail || (craFromEmail === savedCraFromEmail && craSubject === savedCraSubject && craBody === savedCraBody && parseInt(craLockDays, 10) === savedCraLockDays)}
              data-testid="settings-cra-email-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingCraEmail ? "Saving…" : "Save CRA Export config"}
            </button>
          </div>
        </div>
      )}

      {/* CRA Export · Extra Cc recipients (CEO only) ----------------------------- */}
      {isCEO && showSection("cra-export") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="cra-export-cc-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">CRA Export · Extra Cc recipients</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Additional users to add in <strong>Cc</strong> when exporting a CRA. Independent from the invoice email Cc list.
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="settings-cra-cc-list">
            {craCcIds.length === 0 && (
              <span className="text-[11px] text-zinc-400 italic">No additional Cc users configured</span>
            )}
            {craCcIds.map((uid) => {
              const u = userById(uid);
              return (
                <span key={uid} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 text-[11px] rounded font-mono" data-testid={`settings-cra-cc-pill-${uid}`}>
                  {u ? `${u.name} <${u.email}>` : uid}
                  <button onClick={() => removeCraCc(uid)} className="hover:text-red-700" data-testid={`settings-cra-cc-remove-${uid}`}><X size={11} /></button>
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Select value={craPickerUserId} onValueChange={setCraPickerUserId}>
              <SelectTrigger data-testid="settings-cra-cc-picker" className="flex-1 max-w-md text-xs">
                <SelectValue placeholder="Pick a user to add…" />
              </SelectTrigger>
              <SelectContent>
                {allUsers.filter((u) => !craCcIds.includes(u.user_id)).length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No more users to add</div>}
                {allUsers.filter((u) => !craCcIds.includes(u.user_id)).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name} <span className="text-zinc-500">({u.email})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={addCraCc} disabled={!craPickerUserId} data-testid="settings-cra-cc-add" className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
            <button
              onClick={saveCraCc}
              disabled={savingCraCc || JSON.stringify(craCcIds) === JSON.stringify(savedCraCcIds)}
              data-testid="settings-cra-cc-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingCraCc ? "Saving…" : "Save CRA Cc list"}
            </button>
          </div>
        </div>
      )}

      {/* Weekly CRA email — From + Subject/Body template (CEO-only) -------------- */}
      {isCEO && showSection("weekly-cra") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="weekly-cra-email-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Weekly CRA · From email + Subject &amp; body template</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Email used when a collaborator sends a weekly CRA from the timesheet.
            Cc is auto-set to the mission manager + CEO. Variables available:
            <code className="ml-1">{`{{iso_week}}`}</code>, <code>{`{{period}}`}</code>, <code>{`{{mission_title}}`}</code>,
            <code>{`{{client_name}}`}</code>, <code>{`{{user_name}}`}</code>, <code>{`{{total_hours}}`}</code>,
            <code>{`{{year}}`}</code>, <code>{`{{month}}`}</code>.
          </p>
          <div className="grid grid-cols-1 gap-4">
            <div className="border border-zinc-200 bg-zinc-50 rounded-md p-3 text-xs text-zinc-600 leading-relaxed">
              <strong className="text-zinc-900">From address — locked to the sender.</strong><br />
              The Weekly CRA email is always sent from the authenticated user's Gmail
              address — the collaborator when they submit their own CRA, or the CEO/Admin
              when they submit on behalf via impersonation. The global "From email" setting
              is no longer used.
            </div>
          </div>
          <div>
            <Label>Subject template</Label>
            <Input
              data-testid="settings-weekly-cra-subject"
              value={wkSubject}
              onChange={(e) => setWkSubject(e.target.value)}
              placeholder="CRA Semaine {{iso_week}} — {{mission_title}} ({{period}})"
            />
          </div>
          <div>
            <Label>Body template</Label>
            <Textarea
              data-testid="settings-weekly-cra-body"
              rows={9}
              value={wkBody}
              onChange={(e) => setWkBody(e.target.value)}
              placeholder="Bonjour, ..."
              className="font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between border-t border-zinc-100 pt-4 gap-2 flex-wrap">
            <button onClick={resetWkEmail} data-testid="settings-weekly-cra-email-reset" className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
              Reset to default
            </button>
            <button
              onClick={saveWkEmail}
              disabled={savingWkEmail || (wkSubject === savedWkSubject && wkBody === savedWkBody)}
              data-testid="settings-weekly-cra-email-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingWkEmail ? "Saving…" : "Save Weekly CRA config"}
            </button>
          </div>
        </div>
      )}

      {/* Iter-82: Weekly CRA · Extra Cc recipients (CEO only) -------------------- */}
      {isCEO && showSection("weekly-cra") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="weekly-cra-cc-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Weekly CRA · Extra Cc recipients</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. Additional users to add in <strong>Cc</strong> when a collaborator sends a Weekly CRA.
            The mission <strong>manager is always added automatically</strong> — list here every other person
            who must receive a copy.
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="settings-weekly-cra-cc-list">
            {wkCcIds.length === 0 && (
              <span className="text-[11px] text-zinc-400 italic">No additional Cc users — only the mission manager will be Cc'd</span>
            )}
            {wkCcIds.map((uid) => {
              const u = userById(uid);
              return (
                <span key={uid} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 text-[11px] rounded font-mono" data-testid={`settings-weekly-cra-cc-pill-${uid}`}>
                  {u ? `${u.name} <${u.email}>` : uid}
                  <button onClick={() => removeWkCc(uid)} className="hover:text-red-700" data-testid={`settings-weekly-cra-cc-remove-${uid}`}><X size={11} /></button>
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Select value={wkPickerUserId} onValueChange={setWkPickerUserId}>
              <SelectTrigger data-testid="settings-weekly-cra-cc-picker" className="flex-1 max-w-md text-xs">
                <SelectValue placeholder="Pick a user to add…" />
              </SelectTrigger>
              <SelectContent>
                {allUsers.filter((u) => !wkCcIds.includes(u.user_id)).length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">No more users to add</div>}
                {allUsers.filter((u) => !wkCcIds.includes(u.user_id)).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.name} <span className="text-zinc-500">({u.email})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={addWkCc} disabled={!wkPickerUserId} data-testid="settings-weekly-cra-cc-add" className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
            <button
              onClick={saveWkCc}
              disabled={savingWkCc || JSON.stringify(wkCcIds) === JSON.stringify(savedWkCcIds)}
              data-testid="settings-weekly-cra-cc-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingWkCc ? "Saving…" : "Save Weekly CRA Cc list"}
            </button>
          </div>
        </div>
      )}

      {/* Weekly CRA reminders schedule (CEO-only) -------------------------------- */}
      {isCEO && showSection("weekly-cra") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="weekly-cra-reminders-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Weekly CRA · Reminders schedule</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            CEO-only. When a collaborator has logged hours on a mission this ISO week
            but hasn't yet sent the Weekly CRA, fire an in-app reminder at the configured
            day+hour, then an email reminder later in the week. Days are Mon..Sun, hours
            in 24h UTC.
          </p>
          <div className="flex items-center gap-2">
            <input
              id="wkrem-enabled"
              data-testid="settings-weekly-cra-rem-enabled"
              type="checkbox"
              checked={!!wkRemEnabled}
              onChange={(e) => setWkRemEnabled(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="wkrem-enabled" className="cursor-pointer text-sm">Reminders enabled</Label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-zinc-100 rounded-md p-3">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">In-app reminder</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Day</Label>
                  <Select value={String(wkRemInappDay)} onValueChange={(v) => setWkRemInappDay(parseInt(v, 10))}>
                    <SelectTrigger data-testid="settings-weekly-cra-rem-inapp-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Hour (UTC)</Label>
                  <Input data-testid="settings-weekly-cra-rem-inapp-hour" type="number" min="0" max="23" value={wkRemInappHour} onChange={(e) => setWkRemInappHour(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="border border-zinc-100 rounded-md p-3">
              <Label className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Email reminder</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs">Day</Label>
                  <Select value={String(wkRemEmailDay)} onValueChange={(v) => setWkRemEmailDay(parseInt(v, 10))}>
                    <SelectTrigger data-testid="settings-weekly-cra-rem-email-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Hour (UTC)</Label>
                  <Input data-testid="settings-weekly-cra-rem-email-hour" type="number" min="0" max="23" value={wkRemEmailHour} onChange={(e) => setWkRemEmailHour(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
            <button
              onClick={saveWkReminders}
              disabled={savingWkRem || (
                wkRemEnabled === savedWkRemEnabled &&
                parseInt(wkRemInappDay, 10) === savedWkRemInappDay &&
                parseInt(wkRemInappHour, 10) === savedWkRemInappHour &&
                parseInt(wkRemEmailDay, 10) === savedWkRemEmailDay &&
                parseInt(wkRemEmailHour, 10) === savedWkRemEmailHour
              )}
              data-testid="settings-weekly-cra-rem-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingWkRem ? "Saving…" : "Save reminders schedule"}
            </button>
          </div>
        </div>
      )}

      {/* Iter-151: Monthly CRA configuration (final clients) — CEO only -------- */}
      {isCEO && showSection("monthly-cra") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="monthly-cra-email-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Monthly CRA · Email & PDF</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            Sent to final (non-subcontractor) clients. The "From" address is locked at the dialog
            and only editable here by the CEO. The submitter name is printed in the
            "SOUMIS PAR (DIRECTEUR DE MISSION)" box of the PDF, alongside the BAIM stamp.
            Available placeholders: <code>{"{{period}}"}</code>, <code>{"{{mission_title}}"}</code>,
            <code>{"{{client_name}}"}</code>, <code>{"{{contact_gender}}"}</code>, <code>{"{{contact_name}}"}</code>,
            <code>{"{{year}}"}</code>, <code>{"{{month}}"}</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>From email <span className="text-xs text-zinc-500">(must be a verified Gmail "Send mail as" alias)</span></Label>
              <Input data-testid="settings-monthly-cra-from-email" type="email" placeholder="admin@ba-inmotion.com"
                value={monFromEmail} onChange={(e) => setMonFromEmail(e.target.value)} />
            </div>
            <div>
              <Label>Submitter name <span className="text-xs text-zinc-500">(printed in PDF "Soumis par" box)</span></Label>
              <Input data-testid="settings-monthly-cra-submitted-by" placeholder="Sami Khanfir"
                value={monSubmittedBy} onChange={(e) => setMonSubmittedBy(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Subject template</Label>
            <Input data-testid="settings-monthly-cra-subject" value={monSubject} onChange={(e) => setMonSubject(e.target.value)} />
          </div>
          <div>
            <Label>Body template</Label>
            <Textarea data-testid="settings-monthly-cra-body" rows={7} value={monBody} onChange={(e) => setMonBody(e.target.value)} />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
            <button onClick={resetMonEmail} data-testid="settings-monthly-cra-email-reset" className="inline-flex items-center gap-1.5 px-3 h-9 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50">
              Reset to defaults
            </button>
            <button
              onClick={saveMonEmail}
              disabled={savingMonEmail || (
                monFromEmail === savedMonFromEmail &&
                monSubmittedBy === savedMonSubmittedBy &&
                monSubject === savedMonSubject &&
                monBody === savedMonBody
              )}
              data-testid="settings-monthly-cra-email-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingMonEmail ? "Saving…" : "Save Monthly CRA configuration"}
            </button>
          </div>
        </div>
      )}

      {/* Iter-151: Monthly CRA extra Cc list — CEO only ----------------------- */}
      {isCEO && showSection("monthly-cra") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="monthly-cra-cc-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Users size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Monthly CRA · Extra Cc users</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
            The CEO is automatically added as Cc. Select additional internal users who should
            also receive every monthly CRA email sent to a final client.
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="settings-monthly-cra-cc-list">
            {monCcIds.length === 0 && <span className="text-xs text-zinc-400 italic">No extra Cc users configured.</span>}
            {monCcIds.map((uid) => {
              const u = allUsers.find((x) => x.user_id === uid);
              const label = u ? `${u.name} <${u.email}>` : uid;
              return (
                <span key={uid} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 text-[11px] rounded font-mono" data-testid={`settings-monthly-cra-cc-pill-${uid}`}>
                  {label}
                  <button onClick={() => removeMonCc(uid)} className="hover:text-red-700" data-testid={`settings-monthly-cra-cc-remove-${uid}`}><X size={11} /></button>
                </span>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={monPickerUserId} onValueChange={setMonPickerUserId}>
              <SelectTrigger data-testid="settings-monthly-cra-cc-picker" className="flex-1 max-w-md text-xs">
                <SelectValue placeholder="Choose a user…" />
              </SelectTrigger>
              <SelectContent>
                {allUsers.filter((u) => !monCcIds.includes(u.user_id)).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id} className="text-xs">{u.name} — {u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={addMonCc} disabled={!monPickerUserId} data-testid="settings-monthly-cra-cc-add" className="inline-flex items-center gap-1 px-3 h-10 border border-zinc-200 text-xs rounded-md hover:bg-zinc-50 disabled:opacity-50">
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="flex items-center justify-end border-t border-zinc-100 pt-4">
            <button onClick={saveMonCc} disabled={savingMonCc || JSON.stringify(monCcIds) === JSON.stringify(savedMonCcIds)} data-testid="settings-monthly-cra-cc-save" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {savingMonCc ? "Saving…" : "Save Cc list"}
            </button>
          </div>
        </div>
      )}

      {/* Iter-156: Freelance flow templates — CEO only ----------------------- */}
      {isCEO && showSection("freelance-flow") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-6 mb-6" data-testid="freelance-flow-block">
          <div className="flex items-center gap-3 text-zinc-900">
            <Mail size={18} className="text-zinc-700" />
            <h2 className="font-heading font-bold text-lg">Freelance flow · Templates & schedule</h2>
          </div>
          <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">
            Pipeline qui (1) débloque automatiquement la facturation freelance après envoi de la facture client +
            <strong> N jours ouvrés</strong>, (2) envoie un rappel J+3 et J+7 si non soumis, (3) annule + notifie les
            freelances impactés si la facture client est cancelled. Variables disponibles dans tous les templates :
            <code className="ml-1">{"{{user_name}}"}</code>, <code>{"{{mission_title}}"}</code>, <code>{"{{period}}"}</code>,
            <code>{"{{invoice_number}}"}</code>, <code>{"{{actor_name}}"}</code>.
          </p>

          {/* Grace period */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-zinc-100 pt-4">
            <div>
              <Label>Grace period <span className="text-xs text-zinc-500">(working days)</span></Label>
              <Input type="number" min="0" max="60"
                data-testid="settings-freelance-grace-days"
                value={flGraceDays} onChange={(e) => setFlGraceDays(e.target.value)} />
            </div>
            <div className="md:col-span-2 text-xs text-zinc-500 self-end pb-2">
              Mon-Fri excluding holidays of the client's country. Default: 7.
            </div>
          </div>

          {/* Unlock email */}
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <h3 className="font-semibold text-sm text-zinc-800">1. Unlock notification (J+N)</h3>
            <p className="text-xs text-zinc-500">
              Sent automatically to each impacted freelancer when the grace period elapses, asking them to submit
              their invoice via the platform. Sent from the same Gmail that sent the client invoice.
            </p>
            <div>
              <Label>Subject template</Label>
              <Input data-testid="settings-freelance-unlock-subject"
                value={flUnlockSubject} onChange={(e) => setFlUnlockSubject(e.target.value)} />
            </div>
            <div>
              <Label>Body template</Label>
              <Textarea data-testid="settings-freelance-unlock-body" rows={5}
                value={flUnlockBody} onChange={(e) => setFlUnlockBody(e.target.value)} />
            </div>
          </div>

          {/* Cancel email */}
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <h3 className="font-semibold text-sm text-zinc-800">2. Cancellation notification</h3>
            <p className="text-xs text-zinc-500">
              Sent when the client invoice is cancelled — asks freelancers to contact their manager to potentially
              unlock their timesheet for correction. From <strong>CEO</strong> by default; selecting
              <strong> Gestionnaire</strong> changes the From address to the Gestionnaire's mailbox and adds Manager + CEO
              in CC automatically.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From role</Label>
                <Select value={flCancelFromRole} onValueChange={setFlCancelFromRole}>
                  <SelectTrigger data-testid="settings-freelance-cancel-from-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CEO">CEO (default — no CC)</SelectItem>
                    <SelectItem value="GESTIONNAIRE">Gestionnaire (CC: Manager + CEO)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Subject template</Label>
              <Input data-testid="settings-freelance-cancel-subject"
                value={flCancelSubject} onChange={(e) => setFlCancelSubject(e.target.value)} />
            </div>
            <div>
              <Label>Body template</Label>
              <Textarea data-testid="settings-freelance-cancel-body" rows={6}
                value={flCancelBody} onChange={(e) => setFlCancelBody(e.target.value)} />
            </div>
          </div>

          {/* J+3 reminder */}
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <h3 className="font-semibold text-sm text-zinc-800">3. First reminder (J+3 after unlock)</h3>
            <div>
              <Label>Subject template</Label>
              <Input data-testid="settings-freelance-reminder1-subject"
                value={flR1Subject} onChange={(e) => setFlR1Subject(e.target.value)} />
            </div>
            <div>
              <Label>Body template</Label>
              <Textarea data-testid="settings-freelance-reminder1-body" rows={5}
                value={flR1Body} onChange={(e) => setFlR1Body(e.target.value)} />
            </div>
          </div>

          {/* J+7 reminder */}
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <h3 className="font-semibold text-sm text-zinc-800">4. Second reminder (J+7 after unlock)</h3>
            <div>
              <Label>Subject template</Label>
              <Input data-testid="settings-freelance-reminder2-subject"
                value={flR2Subject} onChange={(e) => setFlR2Subject(e.target.value)} />
            </div>
            <div>
              <Label>Body template</Label>
              <Textarea data-testid="settings-freelance-reminder2-body" rows={5}
                value={flR2Body} onChange={(e) => setFlR2Body(e.target.value)} />
            </div>
          </div>

          {/* Iter-221 — Workflow status emails (configurable per transition).
              Sections 5-8 continue the same numbering as the existing 1-4. */}
          <div className="border-t border-zinc-200 pt-4 space-y-3" data-testid="freelance-wf-status-block">
            <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">
              Sections 5 à 8 — emails envoyés à chaque transition de statut d'une facture freelance. Le <strong>From</strong> est
              toujours l'email de la personne qui effectue l'opération. CEO/Gestionnaires sont automatiquement en CC ; vous pouvez
              ajouter des destinataires CC supplémentaires par événement.<br />
              Variables disponibles :
              {(wfMeta.available_variables || []).map((v) => (
                <code key={v} className="ml-1 bg-zinc-100 px-1 rounded">{`{{${v}}}`}</code>
              ))}
              .
            </p>
          </div>
          {(wfMeta.events || []).map((ev, idx) => {
            const draft = wfDraft[ev.event_key] || { subject: "", body: "", extra_cc: "" };
            const setField = (key, val) => setWfDraft((prev) => ({
              ...prev,
              [ev.event_key]: { ...(prev[ev.event_key] || {}), [key]: val },
            }));
            return (
              <details
                key={ev.event_key}
                className="border-t border-zinc-100 pt-4 space-y-3"
                data-testid={`wf-event-${ev.event_key}`}
              >
                <summary className="cursor-pointer flex items-center justify-between -mt-1">
                  <h3 className="font-semibold text-sm text-zinc-800">{`${5 + idx}. ${ev.label}`}</h3>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{ev.event_key}</span>
                </summary>
                <div className="space-y-3 pt-2">
                  <div>
                    <Label>Subject template</Label>
                    <Input
                      data-testid={`wf-subject-${ev.event_key}`}
                      value={draft.subject}
                      onChange={(e) => setField("subject", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Body template</Label>
                    <Textarea
                      data-testid={`wf-body-${ev.event_key}`}
                      rows={8}
                      value={draft.body}
                      onChange={(e) => setField("body", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Extra CC (emails séparés par virgules)</Label>
                    <Input
                      data-testid={`wf-cc-${ev.event_key}`}
                      value={draft.extra_cc}
                      placeholder="finance@cabinet.com, audit@cabinet.com"
                      onChange={(e) => setField("extra_cc", e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => resetWorkflowEventToDefault(ev)}
                      data-testid={`wf-reset-${ev.event_key}`}
                      className="text-[11px] text-zinc-500 hover:text-brand-orange underline"
                    >
                      Réinitialiser subject/body au défaut
                    </button>
                  </div>
                </div>
              </details>
            );
          })}
          <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-4" data-testid="freelance-flow-save-bar">
            <button
              onClick={saveFlowFreelance}
              disabled={savingFl || (!flDirty && !wfDirty)}
              data-testid="settings-freelance-flow-save"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingFl ? "Saving…" : "Save freelance flow"}
            </button>
          </div>
        </div>
      )}

      {/* Iter-130: Invoice numbering floor (CEO only) ----------------------- */}
      {isCEO && showSection("general") && (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="invoice-numbering-floor-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <Hash size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Invoice numbering — starting number per year</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
          Set the minimum invoice number the platform will issue for a given year. Useful when manual invoices were already produced outside the system (e.g. <span className="font-mono">FA-2026-001</span>..<span className="font-mono">003</span> hand-written before go-live → set <strong>2026 → 4</strong> so the next platform-issued invoice is <span className="font-mono">FA-2026-004</span>). The floor never lets you go backwards: it must be strictly greater than the highest invoice already in the database.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="space-y-2">
            <Label className="text-xs">Year</Label>
            <Input
              type="number"
              min="2000" max="2100" step="1"
              value={floorYear}
              onChange={(e) => setFloorYear(parseInt(e.target.value || "0", 10))}
              data-testid="floor-year-input"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Start invoices at FA-{floorYear}-…</Label>
            <Input
              type="number"
              min="1" max="9999" step="1"
              placeholder="e.g. 4"
              value={floorMin}
              onChange={(e) => setFloorMin(e.target.value)}
              data-testid="floor-min-input"
              className="font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={saveInvoiceFloor}
              disabled={savingFloor || !floorMin}
              data-testid="floor-save-btn"
              className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              <Save size={14} /> {savingFloor ? "Saving…" : "Save floor"}
            </button>
            <button
              onClick={clearInvoiceFloor}
              disabled={savingFloor || !(floorPreview?.floors || {})[String(floorYear)]}
              data-testid="floor-clear-btn"
              className="inline-flex items-center gap-2 px-3 h-10 border border-zinc-200 text-xs text-zinc-700 rounded-md hover:bg-zinc-50 disabled:opacity-50"
            >
              <X size={12} /> Clear
            </button>
          </div>
        </div>
        {floorPreview?.preview?.length ? (
          <div className="border-t border-zinc-100 pt-4 space-y-2">
            <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">Live preview</div>
            <div className="space-y-1.5">
              {floorPreview.preview.map((p) => (
                <div key={p.year} data-testid={`floor-preview-${p.year}`} className="flex items-center justify-between text-xs font-mono bg-zinc-50 px-3 py-2 rounded-md">
                  <span className="text-zinc-700">
                    <span className="font-bold">{p.year}</span> · last issued: FA-{p.year}-{String(p.current_max).padStart(3, "0")} · floor: {p.floor || "—"}
                  </span>
                  <span className="text-zinc-900 font-bold">→ next: {p.computed_next}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      )}

      {/* Invoice PDF regeneration -------------------------------------------------- */}
      {showSection("general") && (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6">
        <div className="flex items-center gap-3 text-zinc-900">
          <FileText size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Invoices — PDF regeneration</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">
          Régénérer les PDF de toutes les factures existantes (utile après un changement de gabarit : nouveau filigrane, nouveau logo, etc.).
          Les fichiers locaux sont écrasés. Les copies déjà sents dans Google Drive ne sont pas touchées.
        </p>
        <div className="flex items-center gap-3">
          <button onClick={regenerateInvoicePdfs} disabled={regenRunning} data-testid="settings-regen-invoices" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
            <FileText size={14} /> {regenRunning ? "Regenerating…" : "Regenerate invoice PDFs"}
          </button>
          {regenResult && (
            <span className="text-xs font-mono text-zinc-600" data-testid="settings-regen-result">
              {regenResult.ok} / {regenResult.total} regenerated{regenResult.failed ? ` · ${regenResult.failed} failure(s)` : ""}
            </span>
          )}
        </div>
      </div>
      )}

      {/* Iter-166: the auto-invoice end-of-month block has moved to /admin/auto-invoice → Settings tab. */}
      {isCEO && showSection("general") && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6 mb-6 text-xs text-zinc-500" data-testid="auto-invoice-block-relocated">
          <p>
            <strong>Auto-invoice end-of-month settings</strong> have moved to{" "}
            <a href="/admin/auto-invoice" className="text-blue-700 hover:underline">Admin → Auto-invoice → Settings</a>.
          </p>
        </div>
      )}
    </div>
  );
}
